import { RouterProvider } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { lazy, Suspense, useEffect } from "react";

import { CACHE_OWNER_KEY, clearClientCache } from "./lib/cacheReset";
import { shouldDehydrateQuery } from "./lib/persist";
import { persister, queryClient } from "./lib/queryClient";
import { sessionQueryOptions } from "./lib/queries";
import { router } from "./router";
import { Toaster } from "@/components/ui/sonner";
import "./index.css";

const DEV = process.env.NODE_ENV !== "production";

// Persist the Query cache to localStorage so even a full page reload paints the
// last-known board/header instantly, then revalidates in the background. `buster`
// drops the persisted cache when the app ships a new build (stale shapes).
// Automagic cache buster: the running build's short git SHA, injected at launch
// via BUN_PUBLIC_APP_VERSION (see package.json) and inlined into the bundle by
// Bun. Every deploy ships a new SHA, so persisted caches from an older build are
// dropped instead of rehydrated — no manual bump needed when a query SHAPE
// changes. Falls back to the env name when unset (launched outside the scripts).
const APP_VERSION =
  process.env.BUN_PUBLIC_APP_VERSION ||
  (process.env.NODE_ENV === "production" ? "prod" : "dev");

const persistOptions = {
  persister,
  maxAge: 1000 * 60 * 60 * 24, // 24h
  buster: APP_VERSION,
  // Persist only successful, non-session queries (see shouldDehydrateQuery): a
  // pending query's promise serializes as {} and poisons the whole snapshot, and
  // the session probe must always come fresh from the server.
  dehydrateOptions: { shouldDehydrateQuery },
};

// Lazy + DEV-gated so the devtools are tree-shaken out of production builds.
// Flip SHOW_DEVTOOLS to re-enable the floating panel in development.
const SHOW_DEVTOOLS = false;
const ReactQueryDevtools =
  DEV && SHOW_DEVTOOLS
    ? lazy(() =>
      import("@tanstack/react-query-devtools").then((m) => ({
        default: m.ReactQueryDevtools,
      })),
    )
  : () => null;

/**
 * Wipe a rehydrated cache that belongs to a DIFFERENT user (or to a now-logged-out
 * session) before it's shown. The persisted cache paints on mount, before the
 * session probe resolves; once it does, if the owner doesn't match we clear it
 * (on disk too) and re-seat the session we already have so there's no loading
 * flash. Same-user reloads match the marker and keep their instant paint.
 */
function CacheOwnerGuard() {
  const { data: session, isSuccess } = useQuery(sessionQueryOptions);
  useEffect(() => {
    if (!isSuccess) return; // wait for the session probe to resolve
    const current = session?.id ?? "";
    const stored = window.localStorage.getItem(CACHE_OWNER_KEY) ?? "";
    if (stored === current) return;
    clearClientCache(queryClient, persister);
    queryClient.setQueryData(sessionQueryOptions.queryKey, session ?? null);
    if (current) window.localStorage.setItem(CACHE_OWNER_KEY, current);
    else window.localStorage.removeItem(CACHE_OWNER_KEY);
  }, [isSuccess, session]);
  return null;
}

export function App() {
  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={persistOptions}
    >
      <CacheOwnerGuard />
      <RouterProvider router={router} />
      <Toaster />
      <Suspense>
        <ReactQueryDevtools />
      </Suspense>
    </PersistQueryClientProvider>
  );
}

export default App;
